import { Badge, Box, Button, CloseButton, Group, Pagination, Table, Title, Tooltip } from "@mantine/core";
import { IconXboxX } from "@tabler/icons-react";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "urql";
import Loading from "../Loading";
import { documentTypeLabel, languageLabel, processingStatusLabel, useProcessing } from "../utils";
import DeleteButton from "./DeleteButton";
import classes from "./Documents.module.css";
import ProcessAllButton from "./ProcessAllButton";
import { type DocumentPageQuery, type DocumentPageQueryVariables, documentPageQuery } from "./queries";

function Documents(): JSX.Element {
    const { search } = useLocation();
    const navigate = useNavigate();

    const page = useMemo(() => {
        const p = Number.parseInt(new URLSearchParams(search).get("page") || "", 10);
        return Number.isFinite(p) && p > 0 ? p : 1;
    }, [search]);

    const tag = useMemo(() => {
        const t = new URLSearchParams(search).get("tag");
        return t || undefined;
    }, [search]);

    const [result, execute] = useQuery<DocumentPageQuery, DocumentPageQueryVariables>({
        query: documentPageQuery,
        variables: {
            page: page,
            tag: tag,
        },
    });

    useProcessing(result, execute, (result) =>
        result.data!.documentPage.items.reduce((acc, items) => {
            return acc || items.processingStatus === "PROCESSING";
        }, false),
    );

    // If current page exceeds maxPage (e.g., after deletions), clamp to max and normalize URL
    useEffect(() => {
        if (!result.fetching && result.data) {
            const max = result.data.documentPage.maxPage;
            if (max > 0 && page > max) {
                const params = new URLSearchParams(search);
                params.set("page", String(max));
                navigate({ search: `?${params.toString()}` }, { replace: true });
            }
        }
    }, [result.fetching, result.data, page, navigate, search]);

    const clearTag = () => {
        const params = new URLSearchParams(search);
        params.delete("tag");
        navigate({ search: `?${params.toString()}` });
    };

    const handlePageChange = (p: number) => {
        const params = new URLSearchParams(search);
        params.set("page", String(p));
        navigate({ search: `?${params.toString()}` });
    };

    let main = null;
    if (result.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else if (!result.data || result.data.documentPage.maxPage === 0) {
        main = <Box>No documents in database</Box>;
    } else {
        main = (
            <>
                <Pagination total={result.data.documentPage.maxPage} value={page} onChange={handlePageChange} />
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Type</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Title</Table.Th>
                            <Table.Th>Author</Table.Th>
                            <Table.Th>Source</Table.Th>
                            <Table.Th>Created</Table.Th>
                            <Table.Th>Tokens</Table.Th>
                            <Table.Th>Langauge</Table.Th>
                            <Table.Th>Tags</Table.Th>
                            <Table.Th colSpan={3} />
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {result.data.documentPage.items.map((document) => (
                            <Table.Tr key={document.id}>
                                <Table.Td>{documentTypeLabel(document.documentType)}</Table.Td>
                                <Table.Td>{processingStatusLabel(document.processingStatus)}</Table.Td>
                                <Table.Td>{document.title}</Table.Td>
                                <Table.Td>{document.author}</Table.Td>
                                <Table.Td>{document.publication}</Table.Td>
                                <Table.Td>{new Date(document.created).toLocaleString("en-GB")}</Table.Td>
                                <Table.Td>{document.tokens}</Table.Td>
                                <Table.Td>{languageLabel(document.language)}</Table.Td>
                                <Table.Td>
                                    <Group gap="xs">
                                        {document.tags.map((documentTag) => (
                                            <Tooltip key={documentTag} label={documentTag}>
                                                <Badge
                                                    component={Link}
                                                    to={`?tag=${encodeURIComponent(documentTag)}`}
                                                    size="xs"
                                                    classNames={{ root: classes.tag }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    {documentTag}
                                                </Badge>
                                            </Tooltip>
                                        ))}
                                    </Group>
                                </Table.Td>
                                <Table.Td>
                                    <Button
                                        component={Link}
                                        to={`${document.id}/?page=${page}${tag ? `&tag=${encodeURIComponent(tag)}` : ""}`}
                                    >
                                        Details
                                    </Button>
                                </Table.Td>
                                <Table.Td>
                                    <Button
                                        component={Link}
                                        to={`${document.id}/edit?page=${page}${tag ? `&tag=${encodeURIComponent(tag)}` : ""}`}
                                    >
                                        Edit
                                    </Button>
                                </Table.Td>
                                <Table.Td>
                                    <DeleteButton document={document} />
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
                <Pagination total={result.data.documentPage.maxPage} value={page} onChange={handlePageChange} />
            </>
        );
    }

    return (
        <Box className={classes.grid}>
            <Title order={2}>Knowledge database</Title>
            <Group>
                <Button component={Link} to="search">
                    Search database
                </Button>
                <Button component={Link} to="add">
                    Add document
                </Button>
                <ProcessAllButton />
                {tag && (
                    <Badge
                        size="sm"
                        rightSection={
                            <CloseButton
                                aria-label="Remove tag"
                                size="xs"
                                onClick={clearTag}
                                icon={<IconXboxX size={18} stroke={1.5} color="white" />}
                            />
                        }
                    >
                        {tag}
                    </Badge>
                )}
            </Group>
            {main}
        </Box>
    );
}

export default Documents;
